import { Table } from '@/components/ui/Table'
import { UserIdentity } from '@/components/ui/UserIdentity'
import { ROUTES, buildRoute } from '@/constants/routes'
import { Badge, HStack, IconButton, Text } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { FiExternalLink } from 'react-icons/fi'

const columnHelper = createColumnHelper()

const getFrontendPath = (apiPath) => {
  const pathname = apiPath?.split('?')[0]
  if (!pathname) return null

  if (pathname === '/api/auth/login/' || pathname === '/auth/login/') return ROUTES.LOGIN

  const leadMatch = pathname.match(/^\/api\/v1\/leads\/([^/]+)\/?$/)
  if (leadMatch) return buildRoute(ROUTES.LEADS_EDIT, { uuid: leadMatch[1] })
  if (pathname === '/api/v1/leads/' || pathname === '/api/v1/leads') return ROUTES.LEADS

  const userMatch = pathname.match(/^\/api\/v1\/users\/(\d+)\/?$/)
  if (userMatch) return buildRoute(ROUTES.USERS_DETAIL, { id: userMatch[1] })
  if (pathname === '/api/v1/users/' || pathname === '/api/v1/users') return ROUTES.USERS

  if (pathname === '/api/v1/activity-logs/' || pathname === '/api/v1/activity-logs') {
    return ROUTES.ACTIVITY_LOGS
  }

  return null
}

export function ActivityLogsTable({
  data = [],
  isLoading,
  pageCount,
  totalCount,
  pagination,
  sorting,
  onPaginationChange,
  onSortingChange,
  onSelect,
}) {
  const { t } = useTranslation()
  const actionStyles = {
    0: { bg: '#E3FCEF', color: '#006644' },
    1: { bg: '#DEEBFF', color: '#0052CC' },
    2: { bg: '#FFEBE6', color: '#BF2600' },
    3: { bg: '#F4F5F7', color: '#42526E' },
    4: { bg: '#EAE6FF', color: '#403294' },
  }
  const actionLabels = {
    0: t('audit.create'),
    1: t('audit.update'),
    2: t('audit.delete'),
    3: t('audit.access'),
    4: t('audit.syslog'),
  }
  const logTypeStyles = {
    system: { color: '#403294' },
    user: { color: '#006644' },
  }
  const columns = [
    columnHelper.accessor('action_display', {
      header: t('audit.action'),
      cell: (info) => {
        const log = info.row.original
        const actionStyle = actionStyles[log.action] || actionStyles[3]
        return (
          <Badge
            bg={actionStyle.bg}
            color={actionStyle.color}
            fontSize="11px"
            fontWeight="600"
            px={2}
            py={1}
            borderRadius="3px"
          >
            {actionLabels[log.action] || info.getValue() || log.action || '—'}
          </Badge>
        )
      },
      meta: { width: '140px' },
    }),
    columnHelper.accessor('created_at', {
      header: t('audit.date'),
      cell: (info) => {
        try {
          return (
            <Text
              fontSize="12px"
              cursor="pointer"
              color="#0052CC"
              onClick={() => onSelect(info.row.original.id)}
            >
              {format(new Date(info.getValue()), 'dd.MM.yyyy HH:mm:ss')}
            </Text>
          )
        } catch {
          return <Text fontSize="12px">—</Text>
        }
      },
      meta: { width: '160px' },
    }),
    columnHelper.accessor('object_repr', {
      header: t('audit.object'),
      cell: (info) => (
        <Text
          maxW="280px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {info.getValue() || '—'}
        </Text>
      ),
    }),
    columnHelper.accessor('path', {
      header: t('audit.path'),
      cell: (info) => {
        const path = info.getValue()
        const frontendPath = getFrontendPath(path)
        const href = frontendPath ? new URL(frontendPath, window.location.origin).href : null

        return (
          <HStack gap={1} minW={0}>
            <Text fontSize="12px" color="#5E6C84" truncate>{frontendPath || path || '—'}</Text>
            {href && (
              <IconButton
                as="a"
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={t('audit.openPath')}
                title={t('audit.openPath')}
                variant="ghost"
                size="xs"
              >
                <FiExternalLink />
              </IconButton>
            )}
          </HStack>
        )
      },
      enableSorting: false,
    }),
    columnHelper.accessor('log_type_display', {
      header: t('audit.logType'),
      cell: (info) => {
        const logType = info.row.original.log_type
        const logTypeStyle = logTypeStyles[logType] || { color: '#42526E' }
        return (
          <Text
            color={logTypeStyle.color}
            fontSize="12px"
            fontWeight="600"
          >
            {info.getValue() || logType || '—'}
          </Text>
        )
      },
      meta: { width: '130px' },
    }),
    columnHelper.accessor('actor_display', {
      header: t('audit.actor'),
      cell: (info) => {
        const log = info.row.original
        const actor = log.actor || {}
        return <UserIdentity user={{ ...actor, username: actor.username || log.actor_username }} fallbackName={info.getValue()} />
      },
      meta: { width: '240px' },
    }),
    columnHelper.accessor('remote_addr', {
      header: t('audit.remoteAddress'),
      cell: (info) => <Text fontSize="12px">{info.getValue() || '—'}</Text>,
      enableSorting: false,
    }),
  ]

  return (
    <Table
      data={data}
      columns={columns}
      isLoading={isLoading}
      enableSorting
      enablePagination
      manualPagination
      manualSorting
      pageCount={pageCount}
      totalCount={totalCount}
      getRowId={(row) => String(row.id)}
      state={{ pagination, sorting }}
      onPaginationChange={onPaginationChange}
      onSortingChange={onSortingChange}
    />
  )
}

export default ActivityLogsTable
